import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import React from "react";
import loader, { PageResourceStatus } from "./loader";
import redirects from "./redirects.json";
import { apiRunner } from "./api-runner-browser";
import emitter from "./emitter";
import { RouteAnnouncerProps } from "./route-announcer-props";
import { navigate as reachNavigate } from "@reach/router";
import { globalHistory } from "@reach/router/lib/history";
import { parsePath } from "gatsby-link"; // Convert to a map for faster lookup in maybeRedirect()

var redirectMap = redirects.reduce(function (map, redirect) {
  map[redirect.fromPath] = redirect;
  return map;
}, {});

function maybeRedirect(pathname) {
  var redirect = redirectMap[pathname];

  if (redirect != null) {
    if (process.env.NODE_ENV !== "production") {
      if (!loader.isPageNotFound(pathname)) {
        console.error("The route \"" + pathname + "\" matches both a page and a redirect; this is probably not intentional.");
      }
    }

    window.___replace(redirect.toPath);

    return true;
  } else {
    return false;
  }
}

var onPreRouteUpdate = function onPreRouteUpdate(location, prevLocation) {
  if (!maybeRedirect(location.pathname)) {
    apiRunner("onPreRouteUpdate", {
      location: location,
      prevLocation: prevLocation
    });
  }
};

var onRouteUpdate = function onRouteUpdate(location, prevLocation) {
  if (!maybeRedirect(location.pathname)) {
    apiRunner("onRouteUpdate", {
      location: location,
      prevLocation: prevLocation
    });
  }
};

var navigate = function navigate(to, options) {
  if (options === void 0) {
    options = {};
  } // Support forward/backward navigation with numbers
  // navigate(-2) (jumps back 2 history steps)
  // navigate(2)  (jumps forward 2 history steps)


  if (typeof to === "number") {
    globalHistory.navigate(to);
    return;
  }

  var _parsePath = parsePath(to),
      pathname = _parsePath.pathname;

  var redirect = redirectMap[pathname]; // If we're redirecting, just replace the passed in pathname
  // to the one we want to redirect to.

  if (redirect) {
    to = redirect.toPath;
    pathname = parsePath(to).pathname;
  } // If we had a service worker update, no matter the path, reload window and
  // reset the pathname whitelist


  if (window.___swUpdated) {
    window.location = pathname;
    return;
  } // Start a timer to wait for a second before transitioning and showing a
  // loader in case resources aren't around yet.


  var timeoutId = setTimeout(function () {
    emitter.emit("onDelayedLoadPageResources", {
      pathname: pathname
    });
    apiRunner("onRouteUpdateDelayed", {
      location: window.location
    });
  }, 1000);
  loader.loadPage(pathname).then(function (pageResources) {
    // If no page resources, then refresh the page
    // Do this, rather than simply `window.location.reload()`, so that
    // pressing the back/forward buttons work - otherwise when pressing
    // back, the browser will just change the URL and expect JS to handle
    // the change, which won't always work since it might not be a Gatsby
    // page.
    if (!pageResources || pageResources.status === PageResourceStatus.Error) {
      window.history.replaceState({}, "", location.href);
      window.location = pathname;
      clearTimeout(timeoutId);
      return;
    } // If the loaded page has a different compilation hash to the
    // window, then a rebuild has occurred on the server. Reload.


    if (process.env.NODE_ENV === "production" && pageResources) {
      if (pageResources.page.webpackCompilationHash !== window.___webpackCompilationHash) {
        // Purge plugin-offline cache
        if ("serviceWorker" in navigator && navigator.serviceWorker.controller !== null && navigator.serviceWorker.controller.state === "activated") {
          navigator.serviceWorker.controller.postMessage({
            gatsbyApi: "clearPathResources"
          });
        }

        console.log("Site has changed on server. Reloading browser");
        window.location = pathname;
      }
    }

    reachNavigate(to, options);
    clearTimeout(timeoutId);
  });
};

function shouldUpdateScroll(prevRouterProps, _ref) {
  var _this = this;

  var location = _ref.location;
  var pathname = location.pathname,
      hash = location.hash;
  var results = apiRunner("shouldUpdateScroll", {
    prevRouterProps: prevRouterProps,
    // `pathname` for backwards compatibility
    pathname: pathname,
    routerProps: {
      location: location
    },
    getSavedScrollPosition: function getSavedScrollPosition(args) {
      return _this._stateStorage.read(args);
    }
  });

  if (results.length > 0) {
    // Use the latest registered shouldUpdateScroll result, this allows users to override plugin's configuration
    // @see https://github.com/gatsbyjs/gatsby/issues/12038
    return results[results.length - 1];
  }

  if (prevRouterProps) {
    var oldPathname = prevRouterProps.location.pathname;

    if (oldPathname === pathname) {
      // Scroll to element if it exists, if it doesn't, or no hash is provided,
      // scroll to top.
      return hash ? decodeURI(hash.slice(1)) : [0, 0];
    }
  }

  return true;
}

function init() {
  // The "scroll-behavior" package expects the "action" to be on the location
  // object so let's copy it over.
  globalHistory.listen(function (args) {
    args.location.action = args.action;
  });

  window.___push = function (to) {
    return navigate(to, {
      replace: false
    });
  };

  window.___replace = function (to) {
    return navigate(to, {
      replace: true
    });
  };

  window.___navigate = function (to, options) {
    return navigate(to, options);
  }; // Check for initial page-load redirect


  maybeRedirect(window.location.pathname);
}

var RouteAnnouncer = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(RouteAnnouncer, _React$Component);

  function RouteAnnouncer(props) {
    var _this2;

    _this2 = _React$Component.call(this, props) || this;
    _this2.announcementRef = /*#__PURE__*/React.createRef();
    return _this2;
  }

  var _proto = RouteAnnouncer.prototype;

  _proto.componentDidUpdate = function componentDidUpdate(prevProps, nextProps) {
    var _this3 = this;

    requestAnimationFrame(function () {
      var pageName = "new page at " + _this3.props.location.pathname;

      if (document.title) {
        pageName = document.title;
      }

      var pageHeadings = document.querySelectorAll("#gatsby-focus-wrapper h1");

      if (pageHeadings && pageHeadings.length) {
        pageName = pageHeadings[0].textContent;
      }

      var newAnnouncement = "Navigated to " + pageName;

      if (_this3.announcementRef.current) {
        var oldAnnouncement = _this3.announcementRef.current.innerText;

        if (oldAnnouncement !== newAnnouncement) {
          _this3.announcementRef.current.innerText = newAnnouncement;
        }
      }
    });
  };

  _proto.render = function render() {
    return /*#__PURE__*/React.createElement("div", Object.assign({}, RouteAnnouncerProps, {
      ref: this.announcementRef
    }));
  };

  return RouteAnnouncer;
}(React.Component); // Fire on(Pre)RouteUpdate APIs


var RouteUpdates = /*#__PURE__*/function (_React$Component2) {
  _inheritsLoose(RouteUpdates, _React$Component2);

  function RouteUpdates(props) {
    var _this4;

    _this4 = _React$Component2.call(this, props) || this;
    onPreRouteUpdate(props.location, null);
    return _this4;
  }

  var _proto2 = RouteUpdates.prototype;

  _proto2.componentDidMount = function componentDidMount() {
    onRouteUpdate(this.props.location, null);
  };

  _proto2.componentDidUpdate = function componentDidUpdate(prevProps, prevState, shouldFireRouteUpdate) {
    if (shouldFireRouteUpdate) {
      onRouteUpdate(this.props.location, prevProps.location);
    }
  };

  _proto2.getSnapshotBeforeUpdate = function getSnapshotBeforeUpdate(prevProps) {
    if (this.props.location.pathname !== prevProps.location.pathname) {
      onPreRouteUpdate(this.props.location, prevProps.location);
      return true;
    }

    return false;
  };

  _proto2.render = function render() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, this.props.children, /*#__PURE__*/React.createElement(RouteAnnouncer, {
      location: location
    }));
  };

  return RouteUpdates;
}(React.Component);

export { init, shouldUpdateScroll, RouteUpdates };